import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useClickOutside from "../../../../../hooks/useClickOutside";
import useCustomSnackbar from "../../../../../hooks/useCustomSnackbar";
import { ConfigContext } from "../../../../../utils/ConfigContext";
import {
  downloadData,
  downloadLayerMetadataCSV,
  getAllowedExports,
} from "../../../../../utils/featureTableUtils";
import Loader from "../../../../Dashboard/Editor/Loader/Loader";
import LayerIcon from "../../../../Icons/LayerIcon";
import { IoDownload } from "../../../../LayerListWidget/LayerItem/ListDropdownMenu/ListDropdownMenu";
import {
  StyledMenuItemContainer,
  StyledSidebarMenuContainer,
} from "../../../../LayerListWidget/LayerList-styled";
import { Tooltip } from "../../../../UI";
import SidebarDropdown from "../UI/SidebarDropdown/SidebarDropdown";

const ExportDataItem = ({ layer }) => {
  const [loading, setLoading] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [openSnackbar] = useCustomSnackbar();
  const ref = useRef();

  useClickOutside(
    ref,
    () => {
      setShowExport(false);
    },
    ["mousedown"]
  );

  const { t } = useTranslation("common");
  const { config } = useContext(ConfigContext);

  const handleDownload = useCallback(
    async (downloadType) => {
      setLoading(true);
      const query = layer.createQuery();

      const result = await downloadData({
        layer,
        downloadType,
        t,
        query,
        config,
        columnsOrder: [],
      });

      setLoading(false);
      openSnackbar(result.message, 15000);
    },
    [layer, config, t]
  );

  const allowedExports = useMemo(() => {
    const allowed = getAllowedExports(layer, config) || [];

    const res = allowed.map(({ type, disabled, Icon }) => ({
      title: t(`screen.download.${type}.label`),
      icon: <Icon width="14px" height="14px" />,
      description: t(`screen.download.${type}.description`),
      disabled: disabled,
      isSeparated: type === "metadata",
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (type === "metadata") {
          downloadLayerMetadataCSV(layer, t);
        } else {
          handleDownload(type);
        }
      },
    }));

    return res;
  }, [layer, config, handleDownload, t]);

  const handleToggleExport = useCallback((e) => {
    e.stopPropagation();
    setShowExport((prev) => !prev);
  }, []);

  const fillColor = "var(--fontColor)";
  const circleColor = "var(--elementBackground)";

  return (
    <StyledMenuItemContainer>
      <StyledSidebarMenuContainer>
        <Tooltip
          wrapperStyles={{
            position: "absolute",
            width: "calc(100%)",
            height: "100%",
            cursor: "default",
            left: 0,
            top: 0,
          }}
          content={layer.getLayerInformation(t)}
          placement="right"
          showOnHover
          className="text-elipsis"
          blurred={true}
          offset={18}
        />
        <div>
          <LayerIcon
            config={config}
            t={t}
            fill={fillColor}
            opsColor={fillColor}
            width={22}
            height={22}
            circleColor={circleColor}
            wrapperStyles={{
              borderRadius: "100px",
              background: circleColor,
              width: "22px",
              height: "22px",
              minWidth: "22px",
            }}
            textStyles={{
              width: "22px",
              height: "22px",
              minWidth: "22px",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              textAlign: "center",
              color: fillColor,
              borderRadius: "100px",
              background: circleColor,
              border: "1px solid #eee",
            }}
            layer={layer}
            defaultTitle={layer.getLayerTitle(t)}
          />
        </div>
        <span>{layer.getLayerTitle(t)}</span>
        <div
          style={{
            position: "relative",
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
          }}
          onClick={showExport ? undefined : handleToggleExport}
          ref={ref}
        >
          {loading && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translate(-50%, -50%)",
                top: "50%",
              }}
            >
              <Loader
                position="absolute"
                size="24px"
                bg="transparent"
                loaderStyles={{
                  position: "relative",
                  top: "9px",
                }}
              />
            </div>
          )}
          <IoDownload fill="#8a8a8a" />
        </div>
        {showExport && allowedExports.length > 0 && (
          <SidebarDropdown options={allowedExports} />
        )}
      </StyledSidebarMenuContainer>
    </StyledMenuItemContainer>
  );
};

export default ExportDataItem;
